/**
 * `part_time_weekly_hours` is a free-text field the employer must fill in for a
 * part-time role, and the values they actually type are of two kinds:
 *
 *   - a bare quantity: "30", "30/35", "Circa 21"
 *   - a sentence that already carries its own unit: "20-24 ore settimanali",
 *     "CIRCA 25 ORE SETTIMANALI DI STANDARD"
 *
 * A bare "30" on the page means nothing on its own, so it needs the unit. A
 * sentence that already says "ore settimanali" must be left exactly as typed,
 * or it reads twice.
 *
 * The match is deliberately STRICT — a number, optionally a second number after
 * a slash or a dash, optionally preceded by "circa" — and everything else is
 * shown verbatim. A looser test ("does the text contain the word ore?") would
 * decorate values it does not understand: the field accepts "solo mattine"
 * today, and a future "30 ore mensili" or "6 h al giorno" would come out as a
 * weekly figure that the employer never wrote.
 */
const BARE_QUANTITY = /^(?:circa\s+)?\d{1,2}(?:\s*[/-]\s*\d{1,2})?$/i;

/**
 * Deliberately the exact key and not a `part_time` prefix. There are four other
 * part-time job types (`part_time_horizontal`, `part_time_veritical` — the typo
 * is in the data — and the two weekend ones) and, measured in production on
 * 18/09/2026, NONE of them has ever carried a value in this field: 357 ads have
 * one and all 357 are plain `part_time`. Matching the prefix would widen the
 * rule to types that have nothing to show.
 *
 * It also keeps the field hidden on the 3 `full_time` ads that still carry a
 * leftover value from before their type was changed: a full-time ad must not
 * show a part-time week.
 */
export const isPartTimeJobType = (jobTypeKey?: string | null): boolean => jobTypeKey === 'part_time';

/**
 * Returns the text to show for the weekly commitment, or `null` when there is
 * nothing worth showing. `translate` receives the bare quantity and is expected
 * to place it inside the localised unit, so the word order stays translatable
 * ("30 ore settimanali" / "30 hours per week").
 */
export const formatPartTimeWeeklyHours = (
  rawValue: string | null | undefined,
  jobTypeKey: string | null | undefined,
  translate: (hours: string) => string
): string | null => {
  if (!isPartTimeJobType(jobTypeKey)) return null;

  const value = rawValue?.trim();
  if (!value) return null;

  return BARE_QUANTITY.test(value) ? translate(value) : value;
};
